/**
 * Custom element for variant selection in cart items.
 * Allows users to change the variant of an item in the cart while preserving properties.
 *
 * @class CartVariantSelection
 * @extends {HTMLElement}
 */
class CartVariantSelection extends HTMLElement {
    /**
     * Creates an instance of CartVariantSelection.
     */
    constructor() {
        super();
        this.isProcessing = false;
    }

    /**
     * Called when the element is inserted into the DOM.
     * Sets up event listeners for radio button changes.
     */
    connectedCallback() {
        const radioButtons = this.querySelectorAll('.rb-cart-item-variant-selection__radio');
        radioButtons.forEach(radio => {
            radio.addEventListener('change', this.handleVariantChange.bind(this));
        });
    }

    /**
     * Gets the list of sections that need to be re-rendered after cart updates.
     *
     * @returns {Array<{id: string, section: string, selector: string}>} Array of section configuration objects
     */
    getSectionsToRender() {
        const mainCartItems = document.getElementById('main-cart-items');
        const mainCartFooter = document.getElementById('main-cart-footer');

        const sections = [];

        if (mainCartItems) {
            sections.push({
                id: 'main-cart-items',
                section: mainCartItems.dataset.id,
                selector: '.js-contents',
            });
        }

        if (mainCartFooter) {
            sections.push({
                id: 'main-cart-footer',
                section: mainCartFooter.dataset.id,
                selector: '.js-contents',
            });
        }

        sections.push(
            {
                id: 'cart-icon-bubble',
                section: 'cart-icon-bubble',
                selector: '.shopify-section',
            },
            {
                id: 'cart-live-region-text',
                section: 'cart-live-region-text',
                selector: '.shopify-section',
            }
        );

        return sections;
    }

    /**
     * Extracts inner HTML from a section HTML string using a selector.
     *
     * @param {string} html - HTML string to parse
     * @param {string} selector - CSS selector to find the element
     * @returns {string} Inner HTML of the selected element, or empty string if not found
     */
    getSectionInnerHTML(html, selector) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const element = parsed.querySelector(selector);
        if (!element) {
            return '';
        }
        return element.innerHTML;
    }

    /**
     * Handles variant change when a radio button is selected.
     * Removes the old variant, adds the new variant with preserved properties,
     * and updates all cart sections.
     *
     * @param {Event} event - Change event from the radio button
     * @async
     */
    async handleVariantChange(event) {
        if (this.isProcessing) return;

        const selectedVariantId = event.target.value;
        const currentVariantId = this.dataset.currentVariantId;
        const lineIndex = parseInt(this.dataset.lineIndex, 10);
        const propertiesJson = this.dataset.properties;

        // Don't do anything if the same variant is selected
        if (selectedVariantId === currentVariantId) {
            return;
        }

        this.isProcessing = true;
        this.setLoadingState(true);

        try {
            // Parse properties from JSON string - always try to parse, even if empty
            const properties = this.parseProperties(propertiesJson);

            // Get sections to render
            const sectionsToRender = this.getSectionsToRender();

            // Step 1: Remove the old variant by setting quantity to 0
            await this.removeOldVariant(lineIndex, sectionsToRender);

            // Step 2: Add the new variant with preserved properties
            await this.addNewVariant(selectedVariantId, properties);

            // Step 3: Fetch updated cart to get the latest state with the new variant
            const cartData = await this.fetchCartData();

            // Step 4: Find the newly added item and its index
            const { newCartItem, newItemArrayIndex } = this.findNewCartItem(cartData, selectedVariantId);

            if (!newCartItem) {
                throw new Error('Variant was not found in cart after adding');
            }

            // Step 5: Get updated cart sections
            const parsedState = await this.updateCartSections(
                newItemArrayIndex,
                newCartItem,
                cartData,
                sectionsToRender,
                selectedVariantId
            );

            // Step 6: Update cart empty state and publish events
            this.updateCartState(parsedState);
            this.publishCartUpdate(parsedState);

        } catch (error) {
            this.showError(error.message || 'Failed to exchange variant. Please try again.');
            // Reset to previous selection
            event.target.checked = false;
            const currentRadio = this.querySelector(`[value="${currentVariantId}"]`);
            if (currentRadio) {
                currentRadio.checked = true;
            }
        } finally {
            this.isProcessing = false;
            this.setLoadingState(false);
        }
    }

    /**
     * Parses properties from JSON string, handling HTML entity decoding.
     *
     * @param {string} propertiesJson - JSON string of properties
     * @returns {Object} Parsed properties object, or empty object if parsing fails
     */
    parseProperties(propertiesJson) {
        let properties = {};
        if (propertiesJson) {
            try {
                // The browser automatically decodes HTML entities in data attributes
                // But we need to handle potential double-encoding
                let decodedJson = propertiesJson;

                // Decode HTML entities if present
                const textarea = document.createElement('textarea');
                textarea.innerHTML = decodedJson;
                decodedJson = textarea.value;

                // Try to parse JSON, even if it's '{}'
                const parsed = JSON.parse(decodedJson);

                // Ensure properties is an object
                if (typeof parsed === 'object' && !Array.isArray(parsed)) {
                    properties = parsed;
                } else {
                    properties = {};
                }
            } catch (e) {
                properties = {};
            }
        }
        return properties;
    }

    /**
     * Removes the old variant from the cart by setting quantity to 0.
     *
     * @param {number} lineIndex - Line index of the item to remove (1-based)
     * @param {Array} sectionsToRender - Array of sections to include in the response
     * @returns {Promise<void>}
     * @async
     */
    async removeOldVariant(lineIndex, sectionsToRender) {
        const removeResponse = await fetch(`${routes.cart_change_url}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...fetchConfig().headers
            },
            body: JSON.stringify({
                line: lineIndex,
                quantity: 0,
                sections: sectionsToRender.map((section) => section.section),
                sections_url: window.location.pathname,
            })
        });

        if (!removeResponse.ok) {
            throw new Error('Failed to remove old variant');
        }
    }

    /**
     * Adds the new variant to the cart with preserved properties.
     *
     * @param {string} selectedVariantId - ID of the variant to add
     * @param {Object} properties - Properties to preserve
     * @returns {Promise<void>}
     * @async
     */
    async addNewVariant(selectedVariantId, properties) {
        const addToCartData = {
            id: selectedVariantId,
            quantity: 1
        };

        // Always add properties if they exist (even if empty object, Shopify will handle it)
        // Shopify expects properties as an object with key-value pairs
        if (properties && typeof properties === 'object' && !Array.isArray(properties)) {
            // Only add if there are actual properties to preserve
            // Empty object {} should not be sent as it's not needed
            if (Object.keys(properties).length > 0) {
                addToCartData.properties = properties;
            }
        }

        const addResponse = await fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...fetchConfig().headers
            },
            body: JSON.stringify(addToCartData)
        });

        if (!addResponse.ok) {
            const errorData = await addResponse.json();
            throw new Error(errorData.description || 'Failed to add variant to cart');
        }
    }

    /**
     * Fetches the current cart data.
     *
     * @returns {Promise<Object>} Cart data object
     * @async
     */
    async fetchCartData() {
        const cartResponse = await fetch('/cart.js', {
            headers: { Accept: 'application/json' }
        });

        if (!cartResponse.ok) {
            throw new Error('Failed to fetch updated cart');
        }

        return await cartResponse.json();
    }

    /**
     * Finds the newly added cart item by variant ID.
     *
     * @param {Object} cartData - Cart data object
     * @param {string} selectedVariantId - Variant ID to find
     * @returns {{newCartItem: Object|null, newItemArrayIndex: number}} Object with found item and its index
     */
    findNewCartItem(cartData, selectedVariantId) {
        let newCartItem = null;
        let newItemArrayIndex = -1;

        if (cartData.items && Array.isArray(cartData.items)) {
            newItemArrayIndex = cartData.items.findIndex(item =>
                item.variant_id?.toString() === selectedVariantId.toString()
            );

            if (newItemArrayIndex !== -1) {
                newCartItem = cartData.items[newItemArrayIndex];
            }
        }

        return { newCartItem, newItemArrayIndex };
    }

    /**
     * Updates cart sections with fresh data and re-initializes variant selection elements.
     *
     * @param {number} newItemArrayIndex - Array index of the new item
     * @param {Object} newCartItem - The new cart item object
     * @param {Object} cartData - Full cart data
     * @param {Array} sectionsToRender - Array of sections to update
     * @param {string} selectedVariantId - Selected variant ID
     * @returns {Promise<Object>} Parsed state with cart data and sections
     * @async
     */
    async updateCartSections(newItemArrayIndex, newCartItem, cartData, sectionsToRender, selectedVariantId) {
        // Line index is 1-based (array index + 1)
        const newItemLineIndex = newItemArrayIndex !== -1 ? newItemArrayIndex + 1 : null;

        if (!newItemLineIndex) {
            throw new Error('Could not determine line index for new item');
        }

        const sectionsResponse = await fetch(`${routes.cart_change_url}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...fetchConfig().headers
            },
            body: JSON.stringify({
                line: newItemLineIndex,
                quantity: newCartItem.quantity,
                sections: sectionsToRender.map((section) => section.section),
                sections_url: window.location.pathname,
            })
        });

        if (!sectionsResponse.ok) {
            throw new Error('Failed to fetch updated cart sections');
        }

        const sectionsResponseText = await sectionsResponse.text();
        const sectionsResponseData = JSON.parse(sectionsResponseText);

        // Combine cart data with sections - use the fresh cart data
        const parsedState = {
            ...cartData,
            sections: sectionsResponseData.sections || {}
        };

        sectionsToRender.forEach((section) => {
            this.updateSection(section, parsedState, cartData, selectedVariantId);
        });

        return parsedState;
    }

    /**
     * Updates a single section with new HTML content.
     *
     * @param {Object} section - Section configuration object
     * @param {Object} parsedState - Parsed state with sections
     * @param {Object} cartData - Cart data for updating variant selection elements
     * @param {string} selectedVariantId - Selected variant ID
     */
    updateSection(section, parsedState, cartData, selectedVariantId) {
        // Check if section data exists in response
        if (!parsedState.sections || !parsedState.sections[section.section]) {
            return;
        }

        const sectionHTML = parsedState.sections[section.section];
        const sectionElement = document.getElementById(section.id);

        if (!sectionElement) {
            return;
        }

        try {
            // Parse the section HTML
            const parsedHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
            const sourceSection = parsedHTML.querySelector(`#${section.id}`) || parsedHTML.body.firstElementChild;

            if (!sourceSection) {
                return;
            }

            // Find the target content element
            const sourceContent = sourceSection.querySelector(section.selector);
            const targetContent = sectionElement.querySelector(section.selector);

            if (sourceContent && targetContent) {
                // Update the content - this is the same approach as cart.js
                targetContent.innerHTML = sourceContent.innerHTML;
            } else if (section.id === 'main-cart-footer') {
                // Special handling for footer - update the entire footer content
                const footerContent = sectionElement.querySelector('.js-contents') || sectionElement.querySelector('.cart__footer');
                const sourceFooterContent = sourceSection.querySelector('.js-contents') || sourceSection.querySelector('.cart__footer');

                if (footerContent && sourceFooterContent) {
                    footerContent.innerHTML = sourceFooterContent.innerHTML;
                } else {
                    // Last resort: update entire section
                    sectionElement.innerHTML = sourceSection.innerHTML;
                }
            } else {
                // Last resort: update entire section
                sectionElement.innerHTML = sourceSection.innerHTML;
            }
        } catch (error) {
            // Final fallback: try to update the entire section element
            try {
                const parsedHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
                const sourceSection = parsedHTML.querySelector(`#${section.id}`) || parsedHTML.body.firstElementChild;

                if (sourceSection) {
                    sectionElement.innerHTML = sourceSection.innerHTML;
                }
            } catch (fallbackError) {
                // Silently fail
            }
        }

        // Re-initialize any custom elements in the updated section
        if (section.id === 'main-cart-items') {
            this.reinitializeVariantSelections(sectionElement, cartData, selectedVariantId);
        }
    }

    /**
     * Re-initializes variant selection elements after DOM update.
     *
     * @param {HTMLElement} sectionElement - The section element containing variant selections
     * @param {Object} cartData - Cart data for finding items
     * @param {string} selectedVariantId - Selected variant ID
     */
    reinitializeVariantSelections(sectionElement, cartData, selectedVariantId) {
        // Re-initialize variant selection elements and update data attributes
        sectionElement.querySelectorAll('rb-cart-variant-selection').forEach(el => {
            // Find the new item in the cart to update data attributes
            // Use the fresh cartData we fetched earlier
            // Find the item in cartData by matching key or variant ID
            const itemKey = el.dataset.itemKey;
            const itemArrayIndex = cartData.items?.findIndex(item =>
                item.key === itemKey || item.variant_id?.toString() === selectedVariantId.toString()
            );

            const newItem = itemArrayIndex !== -1 ? cartData.items[itemArrayIndex] : null;

            if (newItem) {
                // Update the current variant ID in the data attribute
                el.dataset.currentVariantId = newItem.variant_id.toString();
                // Line index is 1-based (array index + 1)
                el.dataset.lineIndex = itemArrayIndex !== -1 ? (itemArrayIndex + 1).toString() : el.dataset.lineIndex;
                el.dataset.itemKey = newItem.key;

                // Update properties - always update, even if empty
                try {
                    // Convert properties to JSON
                    const propsObj = {};

                    if (newItem.properties) {
                        if (Array.isArray(newItem.properties)) {
                            // Shopify returns properties as array of [key, value] pairs
                            newItem.properties.forEach(prop => {
                                if (Array.isArray(prop) && prop.length >= 2) {
                                    propsObj[prop[0]] = prop[1];
                                } else if (typeof prop === 'object' && prop !== null) {
                                    // Handle object format
                                    Object.assign(propsObj, prop);
                                }
                            });
                        } else if (typeof newItem.properties === 'object' && newItem.properties !== null) {
                            // Properties as object
                            Object.assign(propsObj, newItem.properties);
                        }
                    }

                    // Always set properties, even if empty (as '{}')
                    el.dataset.properties = JSON.stringify(propsObj);
                } catch (e) {
                    // Set empty object as fallback
                    el.dataset.properties = '{}';
                }
            }

            // The element will re-initialize via connectedCallback
        });
    }

    /**
     * Updates cart empty state classes.
     *
     * @param {Object} parsedState - Parsed state with item_count
     */
    updateCartState(parsedState) {
        // Update cart empty state
        const cartItems = document.querySelector('cart-items');
        if (cartItems) {
            cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
        }

        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) {
            cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        }
    }

    /**
     * Publishes cart update events to notify other components.
     *
     * @param {Object} parsedState - Parsed state with cart data
     */
    publishCartUpdate(parsedState) {
        // Publish cart update event
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-variant-selection',
                cartData: parsedState
            });
        }

        // Also dispatch custom event for compatibility
        document.documentElement.dispatchEvent(
            new CustomEvent('REBOOTS:cartChange', { bubbles: true, detail: { cart: parsedState } })
        );
    }

    /**
     * Sets the loading state of the variant selection form.
     *
     * @param {boolean} isLoading - Whether the form is in loading state
     */
    setLoadingState(isLoading) {
        const form = this.querySelector('.rb-cart-item-variant-selection__form');
        if (form) {
            form.style.opacity = isLoading ? '0.5' : '1';
            form.style.pointerEvents = isLoading ? 'none' : 'auto';
        }

        const radioButtons = this.querySelectorAll('.rb-cart-item-variant-selection__radio');
        radioButtons.forEach(radio => {
            radio.disabled = isLoading;
        });
    }

    /**
     * Shows an error message to the user.
     *
     * @param {string} message - Error message to display
     */
    showError(message) {
        // Create or update error message
        let errorElement = this.querySelector('.rb-cart-item-variant-selection__error');
        if (!errorElement) {
            errorElement = document.createElement('div');
            errorElement.className = 'rb-cart-item-variant-selection__error';
            this.appendChild(errorElement);
        }
        errorElement.textContent = message;
        errorElement.style.display = 'block';

        // Hide error after 5 seconds
        setTimeout(() => {
            if (errorElement) {
                errorElement.style.display = 'none';
            }
        }, 5000);
    }
}

if (!customElements.get('rb-cart-variant-selection')) {
    customElements.define('rb-cart-variant-selection', CartVariantSelection);
}
